import { pluralise } from '@/lib/format';
import { CompactFigure } from '@/components/dashboards/TargetTiles';
import { cx } from '@/lib/cx';
import type { MoverDirection, RevenueCurrency } from '@/lib/use-revenue-analytics';
import {
  DIRECTION_META,
  compactMoney,
  type DirectionSummary,
  type DirectionTone,
} from './top-movers';

/**
 * The five outcome tiles, and the tab's answer to *"every scenario"*.
 *
 * 🔴 **THESE EXIST SO `Stopped` IS NEVER BURIED.** The sections below read in
 * the owner's own order — growing at the top, gone at the bottom — which is
 * right for reading and wrong for the one finding somebody would act on today.
 * A tile counts it, prices it, and makes it one click away. **Delete these and
 * the section ordering hides a lost customer at the foot of a long table.**
 *
 * ⚠️ **EVERY OUTCOME RENDERS, INCLUDING THE EMPTY ONES.** A tile reading
 * *"Unchanged · 0 companies · —"* is a finding. Dropping it because the count
 * is zero makes *"nobody was unchanged"* and *"the tab does not show that"*
 * look identical, and this codebase has already had a working feature reported
 * as deleted for exactly that reason.
 *
 * ── 🔴 WHAT THESE CARDS NO LONGER SAY, AND THE TEST THAT DECIDED IT ─────────
 *
 * Owner's instruction, 11 September 2026: *"the cards we have growing etc etc
 * new simplify them more remove the extra unneccessary data … number should be
 * visible minimal data is required."* So a tile is now the outcome, the count,
 * the figure, and the unit the figure is in.
 *
 * **`DIRECTION_META.meaning` IS GONE FROM THE FACE OF THE TILE.** *"Bought in
 * both cycles, and more in the later one"* is a LEGEND, and a legend repeated
 * on five tiles is four repetitions of one explanation. It survives as the
 * tile's `title`, so the vocabulary is one hover away rather than one paragraph
 * per card — and `DIRECTION_META` itself is untouched, because the Revenue by
 * Product tab reads `meaning` from it too.
 *
 * ⚠️ **TWO THINGS WERE KEPT AGAINST THAT SAME INSTRUCTION, BOTH BY ONE TEST:
 * does deleting this let a reader reach a FALSE conclusion?**
 *
 * 1. **`amountLabel`** — *"more than last cycle"*, *"last cycle, now nothing"*.
 *    It is not a description of the outcome, it is what the FIGURE IS A TOTAL
 *    OF, and the five tiles total five different things by design. Without it
 *    `236,517.02` on the Growing tile reads as what growing companies paid,
 *    which is a number several times larger.
 * 2. **The currency-switch count.** That is a WARNING, not prose: on an
 *    UNCONVERTED response this tab shows one currency at a time, so a company
 *    that moved from dollars to euros reads as a customer lost. Delete it and
 *    somebody telephones an account that never went anywhere.
 *
 *    🔴 **IT IS `0` ON EVERY CONVERTED RESPONSE, AND THE COUNT DISAPPEARS WITH
 *    IT — 2026-09-18.** ⚠️ *SUPERSEDED, the premise: "this tab shows one currency
 *    at a time by decision … this tab was deliberately left filtering."*
 *    `GET /revenue-tracker` converts now, so both currencies are summed into the
 *    base and the company is neither stopped nor new. `currencySwitchHint`
 *    genuinely finds nothing, because there is nothing to find — the warning
 *    withdraws itself when the trap does, and returns against a server that does
 *    not convert. ⛔ **Do not delete the branch on the strength of it not firing
 *    today**; that is the unconverted path losing its only mark.
 */

/*
 * ⚠️ *SUPERSEDED 2026-10-11: each tile was washed in its outcome's soft tone with
 * a coloured border.* The tiles are neutral hairline cards now; the outcome's
 * tone stays on the FIGURE (`TILE_FIGURE`) beside the outcome's written label, so
 * colour is still never the only signal. Kept as a record so the five outcomes
 * stay one decision.
 */
const TILE_TONE: Readonly<Record<DirectionTone, string>> = {
  success: 'border-border bg-surface',
  info: 'border-border bg-surface',
  warning: 'border-border bg-surface',
  danger: 'border-border bg-surface',
  neutral: 'border-border bg-surface',
};

const TILE_FIGURE: Readonly<Record<DirectionTone, string>> = {
  success: 'text-success',
  info: 'text-info',
  warning: 'text-warning',
  danger: 'text-danger',
  neutral: 'text-fg',
};

/**
 * The tile whose outcome the table is currently narrowed to.
 *
 * ⚠️ **NOT COLOUR ALONE.** Each tile already carries its outcome's own tone, so
 * a selected tile tinted a shade differently would be indistinguishable from an
 * unselected neighbour. The ring is a second signal and `aria-pressed` is the
 * third.
 */
const SELECTED_RING = 'bg-surface-active ring-2 ring-fg';

export interface TopMoversSummaryProps {
  summaries: readonly DirectionSummary[];
  currency: RevenueCurrency;
  /** `'all'` when nothing is narrowed. */
  active: MoverDirection | 'all';
  onSelect: (direction: MoverDirection | 'all') => void;
}

export function TopMoversSummary({
  summaries,
  currency,
  active,
  onSelect,
}: TopMoversSummaryProps) {
  return (
    <div
      role="group"
      aria-label="Filter by outcome"
      className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5"
    >
      {summaries.map((summary) => {
        const meta = DIRECTION_META[summary.direction];
        const selected = active === summary.direction;
        /*
         * ⚠️ **`null` STAYS AN EM DASH AND CARRIES NO `title`.** No company
         * landed in this outcome, which is a different fact from one landing
         * here at `0.00` — and there is no exact decimal to reveal, so the
         * attribute is absent rather than empty.
         *
         * ⚠️ **`Shrinking` TOTALS NEGATIVE CHANGES**, so this headline is the
         * one tile figure that carries a sign.
         */
        const figure = summary.amount === null ? null : compactMoney(summary.amount, currency);
        return (
          <button
            key={summary.direction}
            type="button"
            /*
             * `aria-pressed` rather than a tablist: these narrow what the table
             * below shows, they do not label panels sharing one tabstop — which
             * is what every other view switch in this app already does.
             *
             * ⚠️ **THE STATE THEY READ AND WRITE IS THE OUTCOME COLUMN'S OWN
             * FUNNEL**, not a second store beside it. A tile and a header that
             * each remembered an outcome would disagree the first time somebody
             * used the other one.
             */
            aria-pressed={selected}
            /* Re-pressing the active tile clears the filter, so the control can undo itself. */
            onClick={() => onSelect(selected ? 'all' : summary.direction)}
            /*
             * ⚠️ **THE ONLY PLACE THE VOCABULARY IS STILL WRITTEN OUT.** The
             * owner's instruction took it off the face of the tile; it is kept
             * here because *Stopped* meaning "in the later cycle" rather than
             * "ever" is a real question, and a tooltip answers it without
             * putting a paragraph on every card.
             */
            title={meta.meaning}
            /*
             * No focus styles here: `index.css` already gives every `button` a
             * `:focus-visible` outline in the focus-ring token. A second one at
             * a call site is a fork of a decision made once.
             *
             * ⚠️ **THE HOVER LIFT IS THE ONLY THING SAYING THESE ARE PRESSABLE.**
             * A tile carrying a label and a figure reads as a stat card, and a
             * stat card is not something a reader tries to click — so the one
             * route to `Stopped` that does not involve scrolling past four other
             * sections would go unused. The group's `aria-label` says the same
             * thing to a screen reader, which cannot see a shadow.
             *
             * ⚠️ 2026-10-11: the lift is a background step (`hover:bg-surface-hover`)
             * rather than a shadow — cards here are hairline-only. The affordance
             * is kept; only its means changed.
             */
            className={cx(
              'flex min-w-0 flex-col gap-0.5 rounded-md border px-3 py-2.5 text-left',
              'transition-colors duration-150 ease-out motion-reduce:transition-none',
              selected ? SELECTED_RING : cx(TILE_TONE[meta.tone], 'hover:bg-surface-hover'),
              selected && 'border-border',
            )}
          >
            <span className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-medium text-fg-secondary">{meta.label}</span>
              <span className="text-2xs text-fg-subtle">
                {pluralise(summary.count, 'company', 'companies')}
              </span>
            </span>

            {/*
             * ⚠️ **`null` IS NOT ZERO.** No company landed in this outcome, which
             * is a different fact from one landing here at 0.00 — so the figure
             * is an em dash and the line beneath it does not claim a total.
             *
             * The figure is the tile's headline and is sized to be read first:
             * five tiles in a row are scanned by their numbers, and everything
             * else is what the reader falls back to once one of the numbers has
             * caught them. That is the owner's *"number should be visible"*.
             */}
            <span
              className={cx('text-xl leading-tight font-semibold tabular', TILE_FIGURE[meta.tone])}
              title={figure?.title}
            >
              {/* No code SUFFIX here — it read `US$1.2M USD`, the same doubled
                  unit as the other three tabs wearing it as a prefix. See
                  `Money` in `RevenueCompanyTable.tsx`. */}
              {figure === null ? '—' : <CompactFigure parts={figure.parts} />}
            </span>

            {/*
              ⚠️ **THIS LINE QUALIFIES THE FIGURE — it is not a second saying of
              the outcome's name.** The five tiles total five DIFFERENT things:
              `up`/`down` the change, `stopped` the revenue that has gone, `new`
              the revenue that has arrived. Without it the number above is
              ambiguous by a wide margin, which is why it survived the
              instruction that took `meaning` off the card.
            */}
            <span className="text-2xs text-fg-muted">
              {summary.amount === null ? 'None in this comparison' : meta.amountLabel}
            </span>

            {/*
             * 🔴 The currency-switch caveat, counted on the tile as well as on the
             * rows. On `Stopped` it is the difference between a lost customer and
             * an ordinary billing change, and a reader who scans only the tiles
             * would otherwise never meet it. **A WARNING, so it stays.**
             */}
            {summary.switchHints > 0 && (
              <span className="mt-0.5 text-2xs font-medium text-warning">
                {pluralise(summary.switchHints, 'row', 'rows')} may be a currency change, not a
                real move
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
